<template>
  <section>
    <Tabs>
      <TabsHeader tabs-header-class="atb-bg-white">
        <Tab
          v-for="(tab, index) in tabs"
          :key="'Tab' + tab.id"
          :index="index"
          :value="selectedTab"
          :closable="tab.name !== 'All Filters'"
          @tabClick="(index: number) => tabChange(index)"
          @close="(index: number) => closeTab(index, tab)"
        >
          {{ tab.name }}
          <sup v-if="getTabDirtyStatus(tab.id)">*</sup>
        </Tab>
        <confirmation-popup
          v-if="displayConfirmationPopup"
          :index="closeTabIndex"
          @closeActionPopup="displayConfirmationPopup = false"
          @closeConfirmationIndex="(index: number) => tabClose(index, tabs[index])"
        />
      </TabsHeader>
      <TabsBody>
        <TabPanelItem
          v-for="(tab, index) in tabs"
          :key="tab.id"
        >
          <keep-alive
            :key="'keepAlive' + index"
            v-if="index === 0"
          >
            <FiltersList
              ref="filtersListRef"
              :key="'filterList' + tab.id"
              v-show="selectedTab === index"
              :client-id="clientId"
              :filter-id="tab.id"
              @create-new-tab="createNewTab"
              @create="createNewFilter"
              @copy="copyFilter"
              @delete="deleteModalRef?.changeModalVisibility()"
              @show-dependency="
                filtersDependencyModalRef?.changeModalVisibility()
              "
            />
          </keep-alive>
          <keep-alive
            :key="'keepAlive' + tab.id"
            v-else
          >
            <FilterDetails
              :key="'filterDetail' + tab.id"
              v-show="selectedTab === index"
              :current-client-id="clientId"
              :current-filter-id="tab.id"
              :tab-name="tab.name"
              @create-new-tab="createNewTab"
              @refresh-filters-after-delete="refreshFiltersAfterDelete"
            />
          </keep-alive>
        </TabPanelItem>
      </TabsBody>
    </Tabs>
    <FiltersDelete
      @delete="deleteFilter"
      :isUsed="isFilterUsed"
      :entity="'Filters'"
      ref="deleteModalRef"
    />
    <FiltersDependency
      @delete="deleteFilter"
      :massDeleteDependencies="massDeleteDependencies"
      ref="filtersDependencyModalRef"
    />
  </section>
</template>

<script lang="ts" setup>
  import { nextTick, Ref, ref } from 'vue';
  import { storeToRefs } from 'pinia';
  import Tabs from '@atb-ui/eds-vue/src/components/Tabs/Tabs.vue';
  import Tab from '@atb-ui/eds-vue/src/components/Tabs/Tab.vue';
  import TabsHeader from '@atb-ui/eds-vue/src/components/Tabs/TabsHeader.vue';
  import TabsBody from '@atb-ui/eds-vue/src/components/Tabs/TabsBody.vue';
  import TabPanelItem from '@atb-ui/eds-vue/src/components/Tabs/TabPanelItem.vue';
  import FilterDetails from '@/app/features/ams/filters/components/FiltersDetails.vue';
  import FiltersList from '@/app/features/ams/filters/components/FiltersList.vue';
  import FiltersDelete from '@/app/features/ams/shared/DeleteModal.vue';
  import FiltersDependency from '@/app/features/ams/shared/EntityDependency.vue';
  import ConfirmationPopup from '@/app/shared/components/ConfirmationPopup.vue';
  import { useClientStore } from '@/app/features/ams/client/client-store';
  import { useFiltersStore } from '@/app/features/ams/filters/filters-store';
  import { useAlertStore } from '@/app/shared/stores/alert-store';
  import { Motif } from '@/app/shared/enums/motif-enum';
  import { AddTabEvent } from '@/app/shared/types/events/add-tab-event.interface';
  import { ModalActions } from '@/app/shared/types/modal-actions.interface';
  import { useI18n } from 'vue-i18n';
  import { Filters } from '@/app/shared/types/filters.interface';
  import { TabHeader } from '@/app/shared/types/tab-header.interface';
  import { ViewCodeMetaInformation } from '@/app/shared/types/models/view-code-meta-information.interface';

  const { t } = useI18n();
  const filtersStore = useFiltersStore();
  const clientStore = useClientStore();
  const alertStore = useAlertStore();
  // TODO: Create an interface for this attribute or use an existing one
  const filtersListRef: Ref<any> = ref(null);
  const deleteModalRef: Ref<ModalActions | null> = ref(null);
  const filtersDependencyModalRef: Ref<ModalActions | null> = ref(null);
  const closeTabIndex = ref(-1);
  const displayConfirmationPopup = ref(false);
  const clientId = clientStore.selectedClient.id;
  const { selectedTab, massDeleteDependencies, deleteEntityIds, tabs } =
    storeToRefs(filtersStore);

  const getTabDirtyStatus = (filterId: string | number) => {
    let dirtyFlag = false;
    let dirtyTab = filtersStore.filterDetailsMap.get(filterId)?.dirtyTab;
    if (typeof dirtyTab !== 'undefined') {
      for (const [, dirtyTabValue] of dirtyTab ?? new Map().entries()) {
        if (dirtyTabValue) {
          dirtyFlag = true;
        }
      }
    }
    return dirtyFlag;
  };

  const tabChange = (index: number) => {
    selectedTab.value = index;
    if (selectedTab.value === 0) {
      filtersListRef.value[0].refresh();
    }
  };

  const closeTab = (index: number, tab: TabHeader) => {
    closeTabIndex.value = index;
    if (getTabDirtyStatus(tab.id)) {
      displayConfirmationPopup.value = true;
    } else {
      displayConfirmationPopup.value = false;
      tabClose(index, tab);
    }
  };

  const tabClose = async (index: number, tab: TabHeader) => {
    displayConfirmationPopup.value = false;
    filtersStore.removeTab(tab.id);

    const isTabSaved = !isNaN(tab.id) && tab.id !== -1;
    if (isTabSaved) {
      try {
        await filtersStore.unlockFilterOrDs({
          clientId: clientId,
          filterId: tab.id,
          isAutoLock: true,
        });
      } catch (e) {
        displayError(e);
      }
      // refresh list so that auto-lock is removd on ds if exists
      filtersListRef.value[0].refresh();
    }

    //if tab is closed, select prev tab
    if (index === 1 && index <= selectedTab.value) {
      selectedTab.value -= 1;
      nextTick(() => {
        filtersListRef.value[0].refresh();
      });
    } else if (index <= selectedTab.value) {
      selectedTab.value -= 1;
      filtersListRef.value[0].refresh();
    }
  };

  // TODO: Create an interface for this attribute or use an existing one
  const displayError = (error: any) => {
    alertStore.showNotification(
      error.errors[0].message,
      t('attributes'),
      Motif.ERROR
    );
  };

  const createNewTab = async ({
    id,
    name,
    isNew,
    dirtyTab,
  }: AddTabEvent): Promise<void> => {
    await filtersStore.createFilterTab(clientId, {
      id,
      name,
      isNew,
      dirtyTab,
    });
  };

  const copyFilter = async ({
    id,
    defaultValue,
    format,
    description,
    initializationValue,
    codeType,
    longName,
    isStagg,
    length,
  }: ViewCodeMetaInformation) => {
    const defaultData = {
      length,
      defaultValue,
      format,
      shortName: '',
      description,
      initializationValue,
      clientId: clientId,
      codeType: codeType,
      longName: longName,
      isStagg,
      isVisible: true,
      isPrimary: false,
      imported: false,
      versionControls: [],
    };
    await filtersStore.createNewfilterTab(
      clientId,
      {
        id: -1,
        name: '',
        isNew: true,
        dirtyTab: new Map<string, boolean>(),
        isCopied: {
          id: id,
          isCopy: true,
        },
      },
      defaultData
    );
    selectedTab.value = tabs.value.length - 1;
  };

  const createNewFilter = async () => {
    await filtersStore.createNewfilterTab(
      clientId,
      {
        id: -1,
        name: '',
        isNew: true,
        dirtyTab: new Map<string, boolean>(),
        isCopied: {
          id: -1,
          isCopy: false,
        },
      },
      {
        length: 1,
        defaultValue: '',
        format: 'Alphanumeric',
        shortName: '',
        description: '',
        initializationValue: '',
        clientId: clientId,
        codeType: 'Filter',
        longName: '',
        isVisible: true,
        isPrimary: false,
        imported: false,
        isStagg: false,
        versionControls: [],
      }
    );
    selectedTab.value = tabs.value.length - 1;
  };

  const refreshFiltersAfterDelete = () => {
    selectedTab.value = 0;
    filtersListRef.value[0]?.refresh();
    alertStore.showNotification(
      t('filterDeleted'),
      t('Filters'),
      Motif.SUCCESS
    );
  };

  const isFilterUsed = () => {
    const selectedFilters = filtersListRef.value[0].gridApi
      ?.getSelectedRows()
      .map((filter: Filters) => filter.inuseBy);
    return selectedFilters
      ?.map((filter: Array<string>) => {
        return filter?.length > 0;
      })
      .find((validations: boolean) => validations === true);
  };

  const deleteFilter = async () => {
    try {
      const selectedFilters = filtersListRef.value[0].gridApi
        ?.getSelectedRows()
        .map((filter: Filters) => filter.id);

      await filtersStore.deleteFilters(
        clientId,
        deleteEntityIds.value ?? selectedFilters
      );
      alertStore.showNotification(
        t('filtersDeleted'),
        t('filters'),
        Motif.SUCCESS
      );
      filtersListRef.value[0].gridApi?.deselectAll();
      filtersListRef.value[0]?.refresh();
    } catch (e) {
      alertStore.showNotification(t('deleteError'), t('filters'), Motif.ERROR);
    }
  };

  defineExpose({
    createNewTab,
  });
</script>
